import type { FieldDefinition } from '@/components/smart-columns/advanced-filter-builder.component';
import type {
  CustomGraph,
  NotifyConfig,
  TriggerSchedule,
} from '@/types/custom-playbooks';
import type { CrmUserRouteCrm } from '@/store/features/crm.feature';
import { periodPhrase } from '../per-period';

/** Webhook channels retain their channel-only contract; Clu destinations
 *  retain delivery, owner routing and approval settings. */
export function normalizeNotifyConfig(config: NotifyConfig): NotifyConfig {
  const next = { ...config };
  if (next.channel === 'teams') {
    delete next.subject;
    delete next.channelId;
    if (next.webhookId) {
      delete next.teamsChannelId;
      delete next.to;
      delete next.approve;
      delete next.memberId;
      delete next.ownerField;
      delete next.fallback;
    } else {
      delete next.webhookId;
    }
  } else {
    delete next.webhookId;
    delete next.teamsChannelId;
  }
  return next;
}

/** Hidden row fields cannot remain in a Summary's run-only template scope. */
export function changeNotifyDelivery(
  config: NotifyConfig,
  deliver: NonNullable<NotifyConfig['deliver']>
): NotifyConfig {
  if (deliver === 'summary')
    return normalizeNotifyConfig({
      ...config,
      deliver,
      to:
        config.channel !== 'email' && config.to === 'play_owner'
          ? 'play_owner'
          : 'default',
      row: undefined,
      sort: undefined,
      ownerField: undefined,
      fallback: undefined,
      maxMessages: undefined,
      approve: undefined,
    });
  return normalizeNotifyConfig({
    ...config,
    deliver,
    to: config.to ?? 'default',
    ...(deliver === 'list'
      ? {
          row: config.row ?? '{{name}}, {{job_title}} at {{company_name}}',
          maxMessages: undefined,
        }
      : { row: undefined, sort: undefined, approve: undefined }),
  });
}

/** The Notify card's approve state: "Approve to Tax Directors Q4". */
export function notifyApproveWords(name?: string | null): string {
  const trimmed = name?.trim();
  return trimmed ? `Approve to ${trimmed}` : 'Approve to sequence';
}

/** Clu can add the people in a channel digest to a sequence. */
export function notifyApprovable(
  config: NotifyConfig,
  recordKind: string | null
): boolean {
  return (
    config.to !== 'play_owner' &&
    ((config.channel ?? 'slack') === 'slack' ||
      (config.channel === 'teams' &&
        !config.webhookId &&
        !!config.teamsChannelId)) &&
    config.deliver === 'list' &&
    recordKind === 'person'
  );
}

const OWNER_LABELS: Record<string, string> = {
  'crm.owner_id': 'CRM record owner',
  'crm.account.owner_id': 'Account owner',
  'crm.deal.owner_id': 'Deal owner',
  'crm.original.owner_id': 'Old record owner',
  'crm.original.account.owner_id': 'Old account owner',
};

/** Saved references remain visible; only the current registry offers new ones. */
export function notifyOwnerOptions(
  fields: readonly FieldDefinition[],
  saved?: string
) {
  const options = [
    { value: '__record__', label: 'Record owner' },
    ...fields
      .filter(
        (field) =>
          /^(crm|trigger)\./.test(field.id) &&
          !field.id.split('.').some((part) => part.startsWith('_')) &&
          ['string', 'number', 'enum'].includes(field.type)
      )
      .map((field) => ({
        value: field.id,
        label: OWNER_LABELS[field.id] ?? field.label,
      })),
  ];
  if (saved && !options.some((option) => option.value === saved))
    options.push({ value: saved, label: saved });
  return options;
}

/** The Recipients choice: a teammate's Clu DM is the owner DM sent to a
 *  chosen member (`memberId`). */
export type NotifyRecipients = NonNullable<NotifyConfig['to']> | 'teammate';

export function notifyRecipients(config: NotifyConfig): NotifyRecipients {
  if (config.channel === 'teams' && config.webhookId) return 'default';
  return config.to === 'play_owner' && config.memberId !== undefined
    ? 'teammate'
    : (config.to ?? 'default');
}

/** A current original-owner descriptor can default an explicit recipient edit. */
export function changeNotifyRecipients(
  config: NotifyConfig,
  to: NotifyRecipients,
  fields: readonly FieldDefinition[]
): NotifyConfig {
  if (config.channel === 'teams' && config.webhookId)
    return normalizeNotifyConfig(config);
  if (to === 'play_owner' || to === 'teammate')
    return {
      ...config,
      channel: config.channel === 'teams' ? 'teams' : 'slack',
      to: 'play_owner',
      memberId: to === 'teammate' ? (config.memberId ?? '') : undefined,
      channelId: undefined,
      teamsChannelId: undefined,
      webhookId: undefined,
      channelName: undefined,
      ownerField: undefined,
      fallback: undefined,
      approve: undefined,
    };
  return {
    ...config,
    to,
    memberId: undefined,
    ...(to === 'record_owner' &&
    config.ownerField === undefined &&
    notifyOwnerOptions(fields).some(
      (option) => option.value === 'crm.original.owner_id'
    )
      ? { ownerField: 'crm.original.owner_id' }
      : {}),
  };
}

/** Links follow actual CRM ancestors, never an assumed installation. Attio's
 *  link opens its users panel, which is its only settings page. */
export function notifyMappingCrms(
  graph: CustomGraph | null,
  nodeId: string
): CrmUserRouteCrm[] {
  const crms = new Set<CrmUserRouteCrm>();
  const seen = new Set([nodeId]);
  const pending = [nodeId];
  while (pending.length) {
    const id = pending.pop()!;
    for (const edge of graph?.edges ?? []) {
      if (edge.target !== id || seen.has(edge.source)) continue;
      seen.add(edge.source);
      pending.push(edge.source);
      const node = graph?.nodes.find((node) => node.id === edge.source);
      const config = node?.config as Record<string, unknown> | undefined;
      const crm =
        node?.type === 'trigger' && config?.source === 'crm'
          ? (config?.crm as { crmType?: unknown } | undefined)?.crmType
          : node?.type === 'trigger' && config?.source === 'job_change'
            ? (config?.jobChange as { crmType?: unknown } | undefined)?.crmType
            : node?.type === 'crm_action'
              ? config?.crmType
              : undefined;
      if (crm === 'salesforce' || crm === 'hubspot' || crm === 'attio')
        crms.add(crm);
    }
  }
  return [...crms];
}

export function notifyCapPhrase(schedule?: TriggerSchedule) {
  return !schedule || schedule.frequency === 'continuous'
    ? 'in a rolling 24 hours'
    : periodPhrase(schedule);
}
